import React, { useContext, useState } from "react";

import classNames from "classnames";

import styles from "./style.module.scss";
import ContactSupportLink from "components/ContactSupportLink";
import { SpinnerIcon } from "components/Icons";
import { NetworkError } from "@apollo/client/errors";
import { GraphQLFormattedError } from "graphql";

type TLoadingContextError = GraphQLFormattedError | NetworkError | Error;

type TLoadingContext = {
  error: TLoadingContextError | undefined;
};

const LoadingContext = React.createContext<TLoadingContext | undefined>(
  undefined,
);
const SetLoadingContext = React.createContext<
  React.Dispatch<React.SetStateAction<TLoadingContext>> | undefined
>(undefined);

export function useUpdateLoadingContext() {
  const updateFn = useContext(SetLoadingContext);
  if (!updateFn) {
    throw new Error("Must be used withing WithLoadingContext");
  }
  return updateFn;
}

function useLoadingContext() {
  return useContext(LoadingContext);
}

export function WithLoadingContext({
  children,
}: {
  children: React.ReactNode;
}) {
  const [loadingContext, setLoadingContext] = useState<TLoadingContext>({
    error: undefined,
  });
  return (
    <SetLoadingContext.Provider value={setLoadingContext}>
      <LoadingContext.Provider value={loadingContext}>
        {children}
      </LoadingContext.Provider>
    </SetLoadingContext.Provider>
  );
}

function Loading({
  error,
  small,
  className,
}: {
  error?: boolean;
  small?: boolean;
  className?: string;
}) {
  const context = useLoadingContext();
  const errDetail = context?.error;
  return (
    <div
      className={classNames(
        styles.container,
        !small && styles.containerBig,
        className,
      )}
    >
      {error ? (
        <>
          <div className={styles.loading}>Loading...</div>
          <div className={styles.error}>
            {errDetail?.message ?? (
              <>
                Internal Error: Please{" "}
                <ContactSupportLink>contact support</ContactSupportLink>
              </>
            )}
          </div>
        </>
      ) : (
        <SpinnerIcon className="text-[20px]" />
      )}
    </div>
  );
}

export default Loading;
